/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { IconDownload, IconPlus, IconEye, IconEdit, IconSearch, IconX, IconLoader2 } from "@tabler/icons-react";
import ColumnsFilter from "../../../components/common/ColumnsFilter";
import DropdownFilter from "../../../components/common/DropdownFilter";
import ListView from "../../../components/common/ListView";
import NormalSearch from "../../../components/common/NormalSearch";
import { useState, useRef, useEffect, use } from "react";
import CommonModal from "../../../components/common/CommonModal";
import CreateLocation from "./CreateLocation";
import { useForm } from "react-hook-form";
import { PickupFormData } from "../../../types/types";
import {
  useFetchPickupLocationData,
  usePickupLocationCreateMutation,
  useUpdateLocationMutation,
  useUpdatePickupLocationStatus,
} from "../../../apis/pickupApis";
import toast from "react-hot-toast";
import ToggleButton from "../../../components/ToggleButton";
import MessageModal from "../../../components/common/MessageModal";
import Pagination from "../../../components/common/Pagination";
import useDebounce from "../../../utilities/useDebounce";
import formatDate from "../../../utilities/helper";
import NoDataFound from "../../../components/common/NoDataFound";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import EntriesSelector from "../../../components/common/EntriesSelector";
import { handleDownload } from "../../../utilities/downloadHelper";
import { usePermission } from "../../../hooks/usePermissionTabs";
import ButtonStyled from "../../../components/common/ButtonStyled";

type LocationData = {
  id: string;
};

export default function LocationTab({
  setHeaderButtons,
}: {
  setHeaderButtons?: (n: React.ReactNode) => void;
} = {}) {
  const { allowed: canCreateLocation } = usePermission("pickup_locations", "create");
  const { allowed: canUpdateLocation } = usePermission("pickup_locations", "update");

  const [searchFor, setSearchFor] = useState<string>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [allSelectedData, setAllSelectedData] = useState<LocationData[]>([]);
  const [showCheckboxes, setShowCheckboxes] = useState<boolean>(true);
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("");

  const [openShelve, setOpenShelve] = useState<boolean>(false);

  const openShelveToggleModal = () => {
    if (openShelve) {
      setSelectedLocation(null);
      reset({});
    }
    setOpenShelve(!openShelve);
  };
  const { mutate, isPending } = usePickupLocationCreateMutation();
  const { mutate: updateLocation, isPending: isUpdatePending } = useUpdateLocationMutation();
  const debouncedSearchFor = useDebounce(searchFor, 550);
  const {
    data: pickupLocationData,
    refetch: pickupLocationRefetch,
    isFetching,
    isLoading,
  } = useFetchPickupLocationData(pageNo, limit, debouncedSearchFor);
  const { mutate: updatePickupLocationStatus, isPending: activePending } =
    useUpdatePickupLocationStatus();
  const [pickupData, setPickupData] = useState(pickupLocationData);
  const [actionModalVisible, setActionModalVisible] = useState<boolean>(false);
  const [pickupLocationItem, setPickupLocationItem] = useState<any>(null);
  const [status, setStatus] = useState<string>("");
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(
    null
  );

  const [mode, setMode] = useState<string>("");
  const [selectedLocation, setSelectedLocation] = useState<any>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    register,
    setValue,
    clearErrors,
    formState: { errors },
    watch,
    control,
    handleSubmit,
    reset,
  } = useForm<PickupFormData>();

  useEffect(() => {
    pickupLocationRefetch();
  }, [debouncedSearchFor]);

  useEffect(() => {
    if (pickupData?.pickupLocations) {
      setAllSelectedData((prev) => {
        const filteredPrev = prev.filter((item) =>
          selectedRows.includes(item.id)
        );
        const newSelected = pickupData?.pickupLocations.filter(
          (s: any) =>
            selectedRows.includes(s.id) &&
            !filteredPrev.some((item) => item.id === s.id)
        );
        return [...filteredPrev, ...newSelected];
      });
    }
  }, [pickupData?.pickupLocations, selectedRows]);

  const handlePaginationFetch = async (page: number) => {
    setPageNo(page);
    try {
      await pickupLocationRefetch();
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch data");
    }
  };
  useEffect(() => {
    if (pickupLocationData) {
      if (statusFilter) {
        const filteredLocations = (pickupLocationData?.pickupLocations ?? []).filter(
          (loc: any) => loc.status === statusFilter
        );
        setPickupData({
          ...pickupLocationData,
          pickupLocations: filteredLocations,
          total: filteredLocations.length,
          totalPages: Math.ceil(filteredLocations.length / limit) || 1,
        });
      } else {
        setPickupData(pickupLocationData);
      }
    }
  }, [pickupLocationData, statusFilter, limit]);

  useEffect(() => {
    if (!setHeaderButtons) return;
    setHeaderButtons(null);
    return () => setHeaderButtons?.(null);
  }, [setHeaderButtons]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdownIndex(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCreateOrUpdateLocation = (data: any) => {
    if (mode === "edit" && selectedLocation?.id) {
      updateLocation(
        { id: selectedLocation.id, ...data },
        {
          onSuccess: () => {
            toast.success("Location updated successfully");
            pickupLocationRefetch();
            openShelveToggleModal();
            setSelectedLocation(null);
            setMode("add");
            reset();
          },
          onError: (err: any) => toast.error(err.message || "Update failed"),
        }
      );
    } else {
      mutate(data, {
        onSuccess: () => {
          toast.success("Location created successfully");
          pickupLocationRefetch();
          openShelveToggleModal();
        },
        onError: (err: any) => toast.error(err.message || "Create failed"),
      });
    }
  };

  const handleUpdateStatus = async () => {
    if (!pickupLocationItem) return;

    const newStatus =
      pickupLocationItem.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";

    updatePickupLocationStatus(
      { id: pickupLocationItem.id, status: newStatus },
      {
        onSuccess: () => {
          toast.success("Status updated successfully");
          pickupLocationRefetch();
          setActionModalVisible(false);
          setPickupLocationItem(null);
        },
        onError: (err: any) => {
          toast.error(err.message || "Failed to update status");
          setActionModalVisible(false);
        },
      }
    );
  };

  const handleToggleStatus = (location: any) => {
    setPickupLocationItem(location);
    setStatus(location.status);
    setActionModalVisible(true);
  };

  const handleOpenEdit = (location: any) => {
    setMode("edit");
    setSelectedLocation(location);
    setOpenShelve(true);
  };
  const handleOpenView = (location: any) => {
    setMode("view");
    setSelectedLocation(location);
    setOpenShelve(true);
  };

  const [columnsData, setColumnsData] = useState([
    { text: "Select", value: "select", isShow: showCheckboxes },
    { text: "S/N", value: "sn", isShow: true },
    { text: "Location Name", value: "locationName", isShow: true },
    { text: "Full Address", value: "fullAddress", isShow: true },
    { text: "Country", value: "country", isShow: true },
    { text: "City", value: "city", isShow: true },
    { text: "Pincode", value: "pincode", isShow: true },
    { text: "Latitude", value: "latitude", isShow: true },
    { text: "Longitude", value: "longitude", isShow: true },
    { text: "Created Date", value: "createdDate", isShow: true },
    { text: "Status", value: "status", isShow: true },
  ]);
  const setHeadings = (value: string | undefined, checked: boolean) => {
    setColumnsData((prev) =>
      prev.map((col) =>
        col.value === value ? { ...col, isShow: checked } : col
      )
    );
  };

  const handleSelectRow = (id: string) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === pickupData?.pickupLocations?.length) {
      setSelectedRows((prev) =>
        prev.filter(
          (id) => !pickupData.pickupLocations.some((s: any) => s.id === id)
        )
      );
    } else {
      const newSelections =
        pickupData?.pickupLocations
          ?.filter((s: any) => !selectedRows.includes(s.id))
          .map((s: any) => s.id) || [];
      setSelectedRows((prev) => [...prev, ...newSelections]);
    }
  };

  const columns = columnsData
    .filter((c) => c.isShow)
    .map((col) => {
      switch (col.value) {
        case "select":
          return {
            label: (
              <label className="relative inline-flex items-center justify-center cursor-pointer h-5 w-5 mx-auto">
                <input
                  type="checkbox"
                  checked={
                    pickupData?.pickupLocations?.length > 0 &&
                    pickupData?.pickupLocations?.every((s: any) =>
                      selectedRows.includes(s.id)
                    )
                  }
                  onChange={handleSelectAll}
                  className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] 
                                bg-white checked:bg-[#FBD037] checked:text-white disabled:cursor-not-allowed"
                />
                <span
                  className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white peer-checked:opacity-100 opacity-0 pointer-events-none"
                >
                  ✓
                </span>
              </label>
            ),
            render: (s: any) => (
              <div onClick={(e) => e.stopPropagation()}>
                <label className="relative inline-flex items-center justify-center cursor-pointer h-5 w-5 mx-auto">
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(s.id)}
                    onChange={() => handleSelectRow(s.id)}
                    className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] 
                    bg-white checked:bg-[#FBD037] checked:text-white disabled:cursor-not-allowed"
                  />
                  <span
                    className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white peer-checked:opacity-100 opacity-0 pointer-events-none"
                  >
                    ✓
                  </span>
                </label>
              </div>
            ),
          };
        case "sn":
          return {
            label: "#",
            render: (_: any, i: number) => (pageNo - 1) * limit + (i + 1),
          };
        case "fullAddress":
          return {
            label: "Full Address",
            render: (c: any) => (
              <p className="w-64 truncate line-clamp-1 text-left" data-tooltip={c.fullAddress}>
                {c.fullAddress}
              </p>
            ),
          };
        case "locationName":
          return {
            label: "Location Name",
            render: (c: any) => <span className="text-left" data-tooltip={c.locationName}>{c.locationName}</span>,
          };
        case "country":
          return { label: "Country", render: (c: any) => <span className="text-left" data-tooltip={c.country}>{c.country}</span> };
        case "city":
          return { label: "City", render: (c: any) => <span className="text-left" data-tooltip={c.city}>{c.city}</span> };
        case "pincode":
          return { label: "Pincode", render: (c: any) => {
            const val = c.pincode || c.pinCode || "—";
            return <span className="text-left" data-tooltip={val.toString()}>{val}</span>;
          } };
        case "latitude":
          return { label: "Latitude", render: (c: any) => <span className="text-left" data-tooltip={c.latitude}>{c.latitude}</span> };
        case "longitude":
          return { label: "Longitude", render: (c: any) => <span className="text-left" data-tooltip={c.longitude}>{c.longitude}</span> };
        case "createdDate":
          return {
            label: "Created Date",
            render: (c: any) => {
              const val = formatDate(c.createdAt);
              return <span className="text-left" data-tooltip={val}>{val}</span>;
            },
          };
        case "status":
          return {
            label: "Status",
            render: (c: any) => (
              <div onClick={(e) => e.stopPropagation()} className="flex justify-start" data-tooltip={c.status}>
                <ToggleButton
                  size="sm"
                  onChange={() => handleToggleStatus(c)}
                  checked={c.status === "ACTIVE"}
                  disabled={!canUpdateLocation}
                />
              </div>
            ),
          };

        default:
          return { label: "", render: () => null };
      }
    });

  const mapDataToExport = (c: any, index: number) => ({
    "S/N": index + 1,
    "Location Name": c.locationName,
    "Full Address": c.fullAddress,
    Country: c.country,
    City: c.city,
    Latitude: c.latitude,
    Longitude: c.longitude,
    "Created Date": formatDate(c.createdAt),
    Status: c.status,
  });

  return (
    <>
      <div className="relative grid grid-cols-12 gap-6 p-2 bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 w-full rounded-r-3xl rounded-bl-3xl">
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
        <div className={openShelve ? "col-span-12 xl:col-span-7 z-10" : "col-span-12 z-10"}>
          <div
            className="bg-transparent rounded-3xl border border-slate-100 shadow-sm overflow-visible"
            style={{ minHeight: "150px" }}
          >
            {/* Header Toolbar */}
            <div className="px-6 py-1.5">
              <div className="flex flex-col lg:flex-row lg:items-center justify-end gap-4">
                {/* Right side: Toolbar */}
                <div className="flex flex-wrap items-center gap-2.5 sm:justify-end">
                  {/* Search */}
                  <div className="relative flex items-center group">
                    <div
                      onClick={() => !showSearch && setShowSearch(true)}
                      className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-lg bg-white shadow-sm h-8 cursor-pointer ${
                        showSearch ? "w-64 px-2 cursor-default" : "w-8 justify-center hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 transition-all duration-150"
                      }`}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowSearch(!showSearch);
                          if (showSearch) {
                            setSearchFor("");
                            setPageNo(1);
                          }
                        }}
                        className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0 cursor-pointer"
                      >
                        <IconSearch size={17} stroke={2.5} />
                      </button>
                      {showSearch && (
                        <>
                          <input
                            type="text"
                            value={searchFor}
                            onChange={(e) => {
                              setSearchFor(e.target.value);
                              setPageNo(1);
                            }}
                            placeholder="Search..."
                            autoFocus
                            className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none focus:outline-none focus:ring-0"
                          />
                          {searchFor && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSearchFor("");
                                setPageNo(1);
                              }}
                              className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0 cursor-pointer"
                            >
                              <IconX size={14} />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                    {!showSearch && (
                      <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50 animate-in fade-in slide-in-from-bottom-1 duration-100">
                        Search
                      </div>
                    )}
                  </div>

                  {/* Filter (Right side after search) */}
                  <div className="relative select-none group">
                    <button
                      type="button"
                      onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
                      className="px-3.5 py-1.5 bg-white border border-slate-200 text-slate-800 font-bold text-xs rounded-lg flex items-center gap-1.5 hover:bg-slate-50 shadow-sm transition-all duration-150 h-8 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer"
                    >
                      <span>{statusFilter ? statusFilter.toUpperCase() : "Filter"}</span>
                      <span className="text-slate-400 text-[10px]">▼</span>
                    </button>

                    {isFilterDropdownOpen && (
                      <div className="absolute right-0 mt-1 w-32 bg-white border border-slate-200 rounded-xl shadow-lg z-50 py-1 font-bold text-xs text-slate-700">
                        <button
                          type="button"
                          onClick={() => {
                            setStatusFilter("");
                            setPageNo(1);
                            setIsFilterDropdownOpen(false);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-slate-50 transition"
                        >
                          All Statuses
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setStatusFilter("ACTIVE");
                            setPageNo(1);
                            setIsFilterDropdownOpen(false);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-slate-50 transition"
                        >
                          Active
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setStatusFilter("INACTIVE");
                            setPageNo(1);
                            setIsFilterDropdownOpen(false);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-slate-50 transition"
                        >
                          Inactive
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Download */}
                  <div className="relative group">
                    <button
                      type="button"
                      onClick={() =>
                        handleDownload({
                          selectedRows,
                          allSelectedData,
                          setSelectedRows,
                          setAllSelectedData,
                          showCheckboxes: true,
                          setShowCheckboxes: () => {},
                          setColumnsData,
                          mapDataToExport,
                          fileName: "location_list.xlsx",
                        })
                      }
                      className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer"
                    >
                      <IconDownload size={17} stroke={2.5} />
                    </button>
                    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50 animate-in fade-in slide-in-from-bottom-1 duration-100">
                      Export to Excel
                    </div>
                  </div>

                  {/* Entries Limit Selector */}
                  <div className="relative group">
                    <EntriesSelector
                      value={limit}
                      options={[10, 20, 50]}
                      onChange={(val) => {
                        setLimit(val);
                        setPageNo(1);
                      }}
                    />
                    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50 animate-in fade-in slide-in-from-bottom-1 duration-100">
                      Entries Limit
                    </div>
                  </div>

                  {/* Columns filter */}
                  <div className="relative group">
                    <ColumnsFilter
                      triggerClassName="border border-slate-200 bg-white rounded-lg p-1.5 cursor-pointer text-[#212121] hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 transition-all duration-150 h-8 w-8 flex items-center justify-center shadow-sm select-none"
                      columnsData={columnsData}
                      setHeadings={setHeadings}
                    />
                    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50 animate-in fade-in slide-in-from-bottom-1 duration-100">
                      Columns View
                    </div>
                  </div>

                  {/* Add Location button inside the toolbar */}
                  {canCreateLocation && (
                    <ButtonStyled
                      type="button"
                      onClick={() => {
                        setMode("add");
                        setSelectedLocation(null);
                        reset({});
                        setOpenShelve(true);
                      }}
                      variant={openShelve ? "solid" : "outline"}
                      icon={<IconPlus className="w-3.5 h-3.5" />}
                      value="Add Location"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="border-t border-slate-100">
              {isLoading || isFetching ? (
                <TableSkeleton
                  rows={6}
                  columns={columnsData.filter((c) => c.isShow).map((c) => c.text)}
                />
              ) : pickupData?.pickupLocations ? (
                <ListView
                  data={pickupData?.pickupLocations ?? []}
                  columns={columns}
                  onRowClick={(item: any) => handleOpenView(item)}
                  thClassName={openShelve ? "[&_th]:!px-2 bg-transparent" : "bg-transparent"}
                  trClassName="hover:bg-slate-50/50 bg-transparent group transition-colors duration-150"
                  tbodyClassName="bg-transparent"
                  tdClassName={openShelve ? "!px-2 !py-2.5 text-xs" : ""}
                  mainDivClassName="bg-transparent pt-0 rounded-none"
                  mailDivClassName="p-0 rounded-none overflow-x-auto bg-transparent"
                />
              ) : (
                <NoDataFound />
              )}
            </div>
          </div>
        </div>

        {/* Sidebar panel */}
        {openShelve && (
          <div className="col-span-12 xl:col-span-5 h-full">
            <div className="relative border rounded-lg border-gray-200 flex flex-col overflow-hidden bg-transparent w-full h-full xl:h-[calc(100vh-170px)]">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-7 border-b border-gray-200  flex-shrink-0">
                <h2 className="text-xl font-semibold text-[#212121]">
                  {mode === "add"
                    ? "Create Location"
                    : mode === "edit"
                    ? "Edit Location"
                    : "View Location"}
                </h2>
                <div className="flex items-center gap-3">
                  {mode === "view" && (
                    <ButtonStyled
                      type="button"
                      onClick={() => setMode("edit")}
                      variant="outline"
                      value="Edit"
                    />
                  )}
                  {mode !== "view" && (
                    <ButtonStyled
                      type="button"
                      onClick={handleSubmit(handleCreateOrUpdateLocation)}
                      disabled={isPending || isUpdatePending}
                      isPending={isPending || isUpdatePending}
                      variant="outline"
                      value={mode === "edit" ? "Update" : "Create"}
                    />
                  )}
                  <button
                    onClick={openShelveToggleModal}
                    className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors"
                  >
                    <IconX size={18} />
                  </button>
                </div>
              </div>

              {/* Form fields */}
              <div className="flex-1 overflow-y-auto pb-6">
                <CreateLocation
                  register={register}
                  control={control}
                  errors={errors}
                  setValue={setValue}
                  watch={watch}
                  reset={reset}
                  clearErrors={clearErrors}
                  mode={mode}
                  defaultValues={selectedLocation}
                />
              </div>
            </div>
          </div>
        )}

        {/* Pagination Footer - spans full 12 columns at bottom */}
        {pickupData?.pickupLocations && pickupData?.pickupLocations.length > 0 && (
          <div className="col-span-12 z-10 mt-3 px-2 flex justify-end items-center flex-wrap gap-4 select-none">
            {pickupData?.totalPages > 1 && (
              <Pagination
                totalPages={pickupData?.totalPages}
                currentPage={pageNo}
                fetchData={handlePaginationFetch}
              />
            )}
          </div>
        )}
      </div>

      <MessageModal
        open={actionModalVisible}
        onClose={() => setActionModalVisible(false)}
        title="Confirmation Required"
        subtitle="Are you sure you want to perform this action?"
        img={status === "ACTIVE" ? "/img/inactive.svg" : "/img/active.svg"}
        showFooter={true}
        closeButtonText="Cancel"
        actionButtonText="Confirm"
        actionButtonOnClick={handleUpdateStatus}
        isPending={activePending}
        size="md"
      />
    </>
  );
}
